import { useCallback } from 'react'
import {
  useNodeTopContributionsLazyQuery,
  NodeTopContributionsQuery,
  ContributionType,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useExportData } from '../../../../hooks/export-data.hook'

interface UseExportContributionsProps {
  nodeId: string
  nodeName: string
  sortBy?: string
  sort?: string
}

type Contribution =
  NodeTopContributionsQuery['nodeTopContributions']['contributions'][0]

const contributionTypeLabels: Record<ContributionType, string> = {
  [ContributionType.Edit]: 'Edit',
  [ContributionType.Inquiry]: 'Inquiry',
  [ContributionType.Challenge]: 'Challenge',
  [ContributionType.Reserved]: 'Reserved',
  [ContributionType.OwnerEdit]: 'Owner Edit',
}

export function useExportContributions({
  nodeId,
  nodeName,
  sortBy,
  sort,
}: UseExportContributionsProps) {
  const { t } = useTranslation('nodes')
  const [fetchContributions] = useNodeTopContributionsLazyQuery()

  const fetchBatch = useCallback(
    async (offset: number, limit: number) => {
      const { data } = await fetchContributions({
        variables: {
          input: {
            nodeId,
            limit,
            offset,
            sortBy,
            sort,
          },
        },
      })

      if (!data?.nodeTopContributions?.contributions) {
        return null
      }

      return {
        items: data.nodeTopContributions.contributions,
        total: data.nodeTopContributions.total,
      }
    },
    [fetchContributions, nodeId, sortBy, sort]
  )

  const getHeaders = () => [
    t('csv-position'),
    t('csv-cluster'),
    t('csv-type'),
    t('csv-info'),
    t('csv-bumps'),
    t('csv-created-date'),
  ]

  const mapRow = useCallback((contribution: Contribution, index: number) => {
    const contributionInfo =
      contribution.motivation ||
      contribution.input ||
      contribution.originalText ||
      'N/A'
    return [
      (index + 1).toString(), // Position
      contribution.cluster?.topic || 'N/A', // Cluster name
      contributionTypeLabels[contribution.contributionType] ||
        contribution.contributionType, // Type
      contributionInfo, // Info
      (contribution.bumpScore || 0).toString(), // Bump score
      new Date(contribution.createdAt).toISOString().split('T')[0], // Created date (YYYY-MM-DD)
    ]
  }, [])

  const { exportData, isExporting, progress } = useExportData({
    fetchBatch,
    getHeaders,
    mapRow,
    filename: `${nodeName}-contributions`,
  })

  return {
    exportContributions: exportData,
    isExporting,
    progress,
  }
}
